import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  LOCALE_ID,
  OnInit,
} from '@angular/core';
import { ABTest } from '@freelancer/abtest';
import { Auth } from '@freelancer/auth';
import { Datastore } from '@freelancer/datastore';
import {
  ReferralInvitationCheckCollection,
  UsersSelfCollection,
} from '@freelancer/datastore/collections';
import { Feature, FeatureFlagsService } from '@freelancer/feature-flags';
import { ContainerSize } from '@freelancer/ui/container';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import * as Rx from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-navigation',
  template: `
    <ng-container *ngIf="isLoggedIn$ | async; else loggedOutNavigation">
      <app-navigation-primary
        [ngClass]="{ IsMobileSticky: isMobileSticky }"
        [containerSize]="containerSize"
        [darkMode]="darkMode"
        [disablePostProjectButton]="disablePostProjectButton"
        [showGiveButton$]="showGiveButton$"
        [enterpriseNavigation]="enterpriseNavigation"
      ></app-navigation-primary>
    </ng-container>

    <ng-template #loggedOutNavigation>
      <app-navigation-logged-out
        [containerSize]="containerSize"
        [disablePostProjectButton]="disablePostProjectButton"
        [useLoginSignupModal]="useLoginSignupModal"
        [enterpriseNavigation]="enterpriseNavigation"
        [transparent]="transparent"
      ></app-navigation-logged-out>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./navigation.component.scss'],
})
export class NavigationComponent implements OnInit {
  @Input() containerSize: ContainerSize;
  @Input() disablePostProjectButton = false;
  @Input() isMobileSticky: boolean;
  @Input() transparent?: boolean;
  @Input() useLoginSignupModal: boolean;
  @Input() enterpriseNavigation: boolean;

  isLoggedIn$: Rx.Observable<boolean>;
  darkMode: boolean;

  isGiveGetEligible$: Rx.Observable<boolean>;
  userCountry$: Rx.Observable<string>;
  // Whether to show the give button in the primary navbar.
  showGiveButton$: Rx.Observable<boolean>;

  constructor(
    @Inject(UI_CONFIG) private uiConfig: UiConfig,
    @Inject(LOCALE_ID) private locale: string,
    private auth: Auth,
    private abtest: ABTest,
    private datastore: Datastore,
    private featureFlagService: FeatureFlagsService,
  ) {}

  ngOnInit() {
    this.isLoggedIn$ = this.auth.isLoggedIn();
    this.darkMode = this.uiConfig.theme === 'arrow';

    this.isGiveGetEligible$ = this.datastore
      .document<ReferralInvitationCheckCollection>(
        'referralInvitationCheck',
        this.auth.getUserId(),
      )
      .valueChanges()
      .pipe(map(result => result.isEligible));

    this.userCountry$ = this.datastore
      .document<UsersSelfCollection>('usersSelf', this.auth.getUserId())
      .valueChanges()
      .pipe(
        map(result =>
          result.address !== undefined && result.address.country !== undefined
            ? result.address.country
            : '',
        ),
      );

    this.showGiveButton$ = Rx.combineLatest([
      this.isGiveGetEligible$,
      this.userCountry$,
      this.featureFlagService.getFlag(Feature.GIVE_GET),
    ]).pipe(
      switchMap(([isGiveGetEligible, userCountry, featureFlag]) => {
        // Show give button on navbar when the whitelist cookie is set.
        if (this.abtest.isWhitelistUser()) {
          return Rx.of(true);
        }

        return Rx.of(
          // User who is in the US.
          userCountry === 'United States' &&
            // English user.
            this.locale.startsWith('en') &&
            // Have give get feature flag enabled.
            featureFlag &&
            // Eligible to the give get program.
            isGiveGetEligible,
        );
      }),
    );
  }
}
